import { UpdateEventRecordingTranscribeRequest } from '@crew/apis/eventRecordingTranscribe/models/updateEventRecordingTranscribe/request'
import {
  useInsertEventRecordingTranscribeMutation,
  useUpdateEventRecordingTranscribeMutation,
} from '@crew/apis/eventRecordingTranscribe/eventRecordingTranscribeApis'
import { InsertEventRecordingTranscribeRequest } from '@crew/apis/eventRecordingTranscribe/models/insertEventRecordingTranscribe/request'
import { ValidateRules } from '@crew/utils/form'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMemberDataSource } from 'hooks/dataSource/useMemberDataSource'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import {
  ObjectEventMessage,
  notifyEventRecordingTranscribeEvent,
} from 'features/app/states/appSlice'
import { EventRecordingTranscribe } from '@crew/models/domain'
import { NotifyEventType } from 'enums/app'
import { EventRecordingTranscribeItemInputFormProps } from './eventRecordingTranscribeItemInputForm'
import { useTranslation } from '@crew/modules/i18n'
import { EntityType } from '@crew/enums/domain'

export type FormValues = {
  bulkUpdate: boolean
  speakerId: string | null
  speakerName: string | null
  speachContent: string
}

export const useEventRecordingTranscribeItemInputForm = (
  props: EventRecordingTranscribeItemInputFormProps
) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const projectId = useAppSelector((state) => state.eventDetailPage.projectId)
  const speakerDataSource = useMemberDataSource(EntityType.Project, projectId)

  const [
    insertEventRecordingTranscribeMutation,
    { isLoading: isLoadingInsertTranscribe },
  ] = useInsertEventRecordingTranscribeMutation()
  const [
    updateEventRecordingTranscribeMutation,
    { isLoading: isLoadingUpdateTranscribe },
  ] = useUpdateEventRecordingTranscribeMutation()

  // react-hook-formの各種データを取得
  const {
    control,
    reset,
    getValues,
    setValue,
    formState,
    handleSubmit,
    setError,
    clearErrors,
  } = useForm<FormValues>({
    criteriaMode: 'all',
  })

  const insertEventRecordingTranscribe = useCallback(
    async (
      eventRecordingId: string,
      previousPosition: number,
      data: FormValues
    ) => {
      const params: InsertEventRecordingTranscribeRequest = {
        eventRecordingId: eventRecordingId,
        previousPosition: previousPosition,
        speakerId: data.speakerId ?? undefined,
        speakerName: data.speakerName ?? undefined,
        speechContent: data.speachContent,
      }

      // イベント録画文字起こし登録APIリクエスト
      const result = await insertEventRecordingTranscribeMutation(
        params
      ).unwrap()

      const objectEventMessage: ObjectEventMessage<EventRecordingTranscribe> = {
        eventType: NotifyEventType.Inserted,
        id: result.eventRecordingTranscribe?.id as string,
        object: result.eventRecordingTranscribe ?? undefined,
      }

      dispatch(notifyEventRecordingTranscribeEvent(objectEventMessage))
    },
    [dispatch, insertEventRecordingTranscribeMutation]
  )

  const updateEventRecordingTranscribe = useCallback(
    async (
      eventRecordingTranscribesId: string,
      version: number,
      data: FormValues
    ) => {
      const params: UpdateEventRecordingTranscribeRequest = {
        eventRecordingTranscribe: {
          id: eventRecordingTranscribesId,
          speakerId: data.speakerId ?? undefined,
          speakerName: data.speakerName ?? undefined,
          speechContent: data.speachContent,
          batchSpeakerUpdateFlag: data.bulkUpdate,
          version: version,
        },
      }

      // イベント録画文字起こし更新APIリクエスト
      await updateEventRecordingTranscribeMutation(params).unwrap()

      const objectEventMessage: ObjectEventMessage<EventRecordingTranscribe> = {
        eventType: NotifyEventType.Updated,
        id: eventRecordingTranscribesId,
        // updateで複数件更新される場合があるため、objectはundefinedにする
        object: undefined,
      }

      dispatch(notifyEventRecordingTranscribeEvent(objectEventMessage))
    },
    [dispatch, updateEventRecordingTranscribeMutation]
  )

  // 発言者の入力チェック
  const validateSpeaker = useCallback(() => {
    const speakerId = getValues('speakerId')
    const speakerName = getValues('speakerName')

    // フロントエンドでは発言者IDと発言者名の両方が未入力の場合のみをチェックする
    // - バックエンドでは発言者ID・発信者名のいずれかが必ず入力されていることをチェックする
    if (!speakerId && !speakerName) {
      return false
    }

    clearErrors('speakerId')
    return true
  }, [clearErrors, getValues])

  const validateRules: ValidateRules<FormValues> = useMemo(
    () => ({
      bulkUpdate: {},
      speakerId: {
        validate: {
          always: () => validateSpeaker() || t('message.general.required'),
        },
      },
      speakerName: {},
      speachContent: {
        required: t('message.general.required'),
      },
    }),
    [t, validateSpeaker]
  )

  return {
    control,
    reset,
    getValues,
    setValue,
    formState,
    handleSubmit,
    setError,
    clearErrors,

    validateRules,

    speakerDataSource,

    insertEventRecordingTranscribe,
    updateEventRecordingTranscribe,
    isLoadingInsertTranscribe,
    isLoadingUpdateTranscribe,
  }
}
